import {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Grid, MenuItem, Link, Typography} from '@mui/material';
import {ExpansionPanel, InputField, MoneyField, CurrencySelect, AutocompleteField} from '@/components/common';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import type {CompanyAnnual} from '@/services/accountService/companyAnnualService';
import {querySelectionList} from "@/services/accountService/companyService";

const OTHER_NAME_THAN_COMPANY_OPTIONS = ['Association', 'Church', 'Foundation'];

const liabilitiesAmountTip = (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
        <Typography variant="body2">
            {'You can find this information in A&A (formerly M&A), i.e. HKD100'}
        </Typography>
        <Typography variant="body2">
            (你可以在公司章程裏找到, 例如港元100.)
        </Typography>
    </Box>
);

const liabilitiesCurrencyTip = (
    <Typography variant="body2">
        The currency of above amount (以上金額的貨幣)
    </Typography>
);

const otherNameThanCompanyTip = (
    <Typography variant="body2">
        You can follow last year audit, If first audit, just use Company. (可以跟從上年的審計報告找出答案。)
    </Typography>
);

const SECTION88_IRD_URL = 'https://www.ird.gov.hk/eng/tax/ach_search.htm';

const section88Tip = (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
        <Typography variant="body2">
            {'Please search the list of section 88 company in the '}
            <Link href={SECTION88_IRD_URL} target="_blank" rel="noopener noreferrer">
                IRD website
            </Link>
            .
        </Typography>
        <Typography variant="body2">
            {'(可以在'}
            <Link href={SECTION88_IRD_URL} target="_blank" rel="noopener noreferrer">
                稅局網站
            </Link>
            {'找到慈善團體列表。)'}
        </Typography>
    </Box>
);

type Props = {
    annualId: number;
    companyId?: number;
    onChange?: (patch: Partial<CompanyAnnual>) => void;
    disableAccordion?: boolean;
    hideOverseasProfitsClaims?: boolean;
    taxFileNoError?: string;
    initialData?: {
        limitByGuarantee?: number;
        limitByGuaranteeLocked?: boolean;
        changeOfAddress?: number;
        lastAddress?: string | null;
        taxFileNo?: string | null;
    } | null;
};

export default function EpCompanyDetails({
                                             annualId,
                                             companyId,
                                             onChange,
                                             disableAccordion = false,
                                             hideOverseasProfitsClaims = false,
                                             taxFileNoError,
                                             initialData
                                         }: Props) {
    const getCompanyDetailsInfo = useCompanyAnnualStore(s => s.getCompanyDetailsInfo);
    const annualFromStore = useCompanyAnnualStore(s => s.byAnnualId[Number(annualId)]);
    const setAnnualCache = useCompanyAnnualStore(s => s.setAnnual);
    // Company selection list and chosen company
    const [companyOptions, setCompanyOptions] = useState<Array<{
        companyId: number;
        companyName: string;
        companyChineseName?: string;
        industry?: number[];
        industryName?: string;
        inCorpDate?: string;
        registeredOffice?: string;
        br?: string;
    }>>([]);
    const [local, setLocal] = useState({
        companyId: undefined as number | undefined,
        offShoreClaim: undefined as number | undefined,
        limitByGuarantee: 0 as number | undefined, // Default to No
        limitByGuaranteeLocked: false as boolean | undefined,
        liabilitiesCurrency: null as string | null,
        liabilitiesAmount: 0,
        otherNameThanCompany: null as string | null,
        section88: 0 as number | undefined,
        taxFileNo: null as string | null,
        changeOfAddress: undefined as number | undefined,
        changeDate: null as string | null,
        lastAddress: null as string | null,
    });

    const [br, setBr] = useState<string>('');
    const taxFileSeedAppliedRef = useRef<string | null>(null);

    const selectedCompany = useMemo(() => {
        const id = Number(local.companyId);
        if (!id) return null;
        return companyOptions.find(x => x.companyId === id) || null;
    }, [local.companyId, companyOptions]);

    useEffect(() => {
        // annual 切换时允许重新种子
        taxFileSeedAppliedRef.current = null;
    }, [annualId]);

    useEffect(() => {
        if (initialData) {
            setLocal(prev => ({
                ...prev,
                limitByGuarantee: initialData.limitByGuarantee ?? prev.limitByGuarantee,
                limitByGuaranteeLocked: initialData.limitByGuaranteeLocked ?? prev.limitByGuaranteeLocked,
                changeOfAddress: initialData.changeOfAddress ?? prev.changeOfAddress,
                lastAddress: initialData.lastAddress ?? prev.lastAddress,
            }));

            // taxFileNo 仅从导入种子应用一次，避免 Create GL 里 form 回灌把 31 冲成 3
            const seed = initialData.taxFileNo;
            if (seed && taxFileSeedAppliedRef.current !== seed) {
                taxFileSeedAppliedRef.current = seed;
                setLocal(prev => ({...prev, taxFileNo: seed}));
                onChange?.({taxFileNo: seed});
            }
            return;
        }

        let mounted = true;
        (async () => {
            const v = await getCompanyDetailsInfo(annualId);

            if (!mounted) return;

            // 清理数据，确保 select 组件只接收有效值
            const cleanedData = {
                ...v,
                // offShoreClaim 只允许 0, 1 或 undefined
                offShoreClaim: (v.offShoreClaim === 0 || v.offShoreClaim === 1) ? v.offShoreClaim : undefined,
                // section88: null/invalid → 0
                section88: Number(v.section88) === 1 ? 1 : 0,
                // 确保 liabilities 字段正确设置
                liabilitiesCurrency: v.liabilitiesCurrency || 'HKD',
                liabilitiesAmount: v.liabilitiesAmount || 0,
            };

            setLocal(cleanedData);
        })();
        return () => {
            mounted = false;
        };
    }, [annualId, getCompanyDetailsInfo, initialData?.limitByGuarantee, initialData?.limitByGuaranteeLocked, initialData?.changeOfAddress, initialData?.lastAddress, initialData?.taxFileNo]);

    // Reactively sync company details fields when store data changes (e.g. from Content Sheet import)
    // taxFileNo 不从 store 回写：用户编辑 / Create GL 打字时 store 可能仍是旧个位数
    useEffect(() => {
        if (!annualFromStore) return;
        setLocal(prev => {
            const next = {
                ...prev,
                limitByGuarantee: annualFromStore.limitByGuarantee ?? prev.limitByGuarantee,
                limitByGuaranteeLocked: annualFromStore.limitByGuaranteeLocked ?? prev.limitByGuaranteeLocked,
                liabilitiesCurrency: annualFromStore.liabilitiesCurrency ?? prev.liabilitiesCurrency,
                liabilitiesAmount: annualFromStore.liabilitiesAmount ?? prev.liabilitiesAmount,
                otherNameThanCompany: annualFromStore.otherNameThanCompany ?? prev.otherNameThanCompany,
                section88: Number(annualFromStore.section88) === 1 ? 1 : 0,
                offShoreClaim: annualFromStore.offShoreClaim ?? prev.offShoreClaim,
            };
            if (
                next.limitByGuarantee === prev.limitByGuarantee &&
                next.limitByGuaranteeLocked === prev.limitByGuaranteeLocked &&
                next.liabilitiesCurrency === prev.liabilitiesCurrency &&
                next.liabilitiesAmount === prev.liabilitiesAmount &&
                next.otherNameThanCompany === prev.otherNameThanCompany &&
                next.section88 === prev.section88 &&
                next.offShoreClaim === prev.offShoreClaim
            ) return prev;
            return next;
        });
    }, [annualFromStore]);

    useEffect(() => {
        (async () => {
            try {
                const res: any = await querySelectionList();
                const data = res?.data ?? res;
                if (Array.isArray(data)) {
                    if (companyId) {
                        const only = data.filter((c: any) => (c?.companyId ?? c?.id) === companyId);
                        const fallback = data[0] ? [{
                            companyId: data[0].companyId ?? data[0].id,
                            companyName: data[0].companyName ?? data[0].name,
                            companyChineseName: data[0].companyChineseName,
                            industry: data[0].industry,
                            industryName: data[0].industryName,
                            inCorpDate: data[0].inCorpDate,
                            registeredOffice: data[0].registeredOffice,
                            br: data[0].br,
                        }] : [];
                        const opts = only.length > 0 ? only : fallback;
                        setCompanyOptions(opts);
                        // Initialize into local state
                        emit({companyId: companyId});
                        setBr(opts[0].br);
                    } else {
                        setCompanyOptions(data);
                        // If local has no companyId yet, default to the first option
                        const firstId = data[0]?.companyId ?? data[0]?.id;
                        if ((local.companyId == null || Number.isNaN(local.companyId)) && firstId != null) {
                            emit({companyId: Number(firstId) as any});
                        }
                    }
                }
            } catch (e) {
                // ignore silently for now
            }
        })();
    }, [companyId, local.companyId])

    const emit = (patch: Partial<typeof local>) => {
        // Same merge + cache payload as before; only move setAnnualCache out of the
        // setLocal updater so Zustand does not notify peers (e.g. EpPeriod) mid-render.
        setLocal(prev => ({...prev, ...patch}));
        setAnnualCache({
            ...((useCompanyAnnualStore.getState().byAnnualId[Number(annualId)] as any)
                || ({annualId: Number(annualId)} as any)),
            annualId: Number(annualId),
            ...(patch as any),
        } as CompanyAnnual);
        onChange?.(patch as Partial<CompanyAnnual>);
    };

    // Pass inCorpDate to parent when company is selected
    useEffect(() => {
        if (selectedCompany?.inCorpDate) {
            onChange?.({inCorpDate: selectedCompany.inCorpDate} as Partial<CompanyAnnual>);
        }
    }, [selectedCompany?.inCorpDate]);

    // Auto-fill BR value when company is selected
    useEffect(() => {
        if (selectedCompany?.br) {
            const newBr = selectedCompany.br;
            setBr(newBr);

            // If taxFileNo is empty or doesn't have the correct BR suffix, update it
            const cleanBr = newBr.replace(/-$/, ''); // Remove trailing dash
            const brCore = cleanBr.replace(/[-\s]000$/i, '');
            const currentTaxFileNo = local.taxFileNo;

            // Reconstruct taxFileNo if needed
            if (!currentTaxFileNo || !(currentTaxFileNo.includes(cleanBr) || currentTaxFileNo.includes(brCore))) {
                // Extract prefix from existing taxFileNo if it exists
                let existingPrefix = '';
                if (currentTaxFileNo) {
                    const parts = currentTaxFileNo.split('/');
                    existingPrefix = parts[0] || '';
                }

                // If prefix is empty, save only BR value (without leading /)
                // Otherwise save prefix/BR format
                if (cleanBr) {
                    const newTaxFileNo = existingPrefix ? `${existingPrefix}/${cleanBr}` : cleanBr;
                    emit({taxFileNo: newTaxFileNo});
                }
            }
        }
    }, [selectedCompany?.br, local.taxFileNo]);

    /** BR for Tax File No right side: hide trailing -000 / 000 when file-type prefix (e.g. 22) is present */
    const taxFileBrDisplay = useMemo(() => {
        const cleanBr = (br || selectedCompany?.br || '').replace(/-$/, '');
        const taxFileNo = local.taxFileNo || '';
        const prefix = taxFileNo.includes('/') ? (taxFileNo.split('/')[0] || '').trim() : '';
        if (prefix) {
            return cleanBr.replace(/[-\s]000$/i, '');
        }
        return cleanBr;
    }, [br, selectedCompany?.br, local.taxFileNo]);

    return (
        <ExpansionPanel
            title="Company Details (公司詳情)"
            expanded={disableAccordion ? true : undefined}
            onChange={disableAccordion ? () => {
            } : undefined}
        >
            <Grid container spacing={0.5}>
                <Grid size={6}>
                    {(() => {
                        const companyNameDisplay = (companyId !== undefined && selectedCompany != null);
                        // 只有当 companyId 存在于 options 中时才使用它，否则使用空字符串
                        const isValidOption = local.companyId != null &&
                            companyOptions.some(co => co.companyId === local.companyId);
                        const selectValue = isValidOption ? local.companyId : '';

                        return (
                            <InputField select fullWidth variant="outlined" label="Company Name"
                                        display={companyNameDisplay ? true : undefined}
                                        value={selectValue}
                                        onChange={(e) => {
                                            const value = e.target.value as any;
                                            const num = value === '' ? undefined : Number(value);
                                            emit({companyId: (num as any)});
                                        }}>
                                {companyOptions.length > 1 && (
                                    <MenuItem dense value="">Select</MenuItem>
                                )}
                                {companyOptions.map(co => (
                                    <MenuItem dense key={co.companyId} disabled={companyNameDisplay}
                                              value={co.companyId}>{co.companyName}</MenuItem>
                                ))}
                            </InputField>
                        );
                    })()}
                </Grid>
                {selectedCompany == null ? (
                    <Grid size={6}></Grid>
                ) : (
                    <>
                        <Grid size={6}>
                            <InputField fullWidth variant="outlined" display
                                        label="Company Chinese Name"
                                        value={selectedCompany.companyChineseName}
                                        onChange={() => {
                                        }}/>
                        </Grid>
                        <Grid size={6}>
                            <InputField fullWidth variant="outlined" display
                                        label="Industry"
                                        value={selectedCompany.industryName}
                                        onChange={() => {
                                        }}/>
                        </Grid>
                        <Grid size={12}>
                            <InputField fullWidth variant="outlined" display
                                        label="Registered office"
                                        value={
                                            annualFromStore?.currentRegisteredOffice
                                            ?? annualFromStore?.registeredOffice
                                            ?? selectedCompany.registeredOffice
                                            ?? ''
                                        }
                                        onChange={() => {
                                        }}/>
                        </Grid>
                        <Grid size={6}>
                            <InputField fullWidth variant="outlined" display
                                        label="Business Registration No."
                                        value={selectedCompany.br}
                                        onChange={() => {
                                        }}/>
                        </Grid>

                        <Grid size={6}>
                            <InputField fullWidth variant="outlined" display
                                        label="Incorporation Date"
                                        value={selectedCompany.inCorpDate}
                                        popoverContent={
                                            <Typography variant="body2" sx={{whiteSpace: 'nowrap'}}>
                                                You can find it in CR search. (你可以在公司注冊處網站找到。)
                                            </Typography>
                                        }
                                        onChange={() => {
                                        }}/>
                        </Grid>
                    </>
                )}
                {!hideOverseasProfitsClaims && (
                    <Grid size={6 as any}>
                        <InputField select fullWidth variant="outlined" label="Overseas Profits Claims (海外利得申索)"
                                    value={
                                        local.offShoreClaim === undefined ||
                                        local.offShoreClaim === null ||
                                        (local.offShoreClaim !== 0 && local.offShoreClaim !== 1)
                                            ? ''
                                            : local.offShoreClaim
                                    }
                                    onChange={(e) => emit({offShoreClaim: e.target.value === '' ? undefined : Number(e.target.value) as any})}>
                            <MenuItem dense value={0}>No</MenuItem>
                            <MenuItem dense value={1}>Yes</MenuItem>
                        </InputField>
                    </Grid>
                )}
                <Grid size={6 as any}>
                    <InputField select fullWidth variant="outlined"
                                label="Is it limited by guarantee？ (是否擔保有限公司?)"
                                value={local.limitByGuarantee ?? 0}
                                sx={local.limitByGuaranteeLocked ? {pointerEvents: 'none', opacity: 0.6} : undefined}
                                onChange={(e) => {
                                    if (local.limitByGuaranteeLocked) return;
                                    emit({limitByGuarantee: Number(e.target.value) as any});
                                }}>
                        <MenuItem dense value={0}>No</MenuItem>
                        <MenuItem dense value={1}>Yes</MenuItem>
                    </InputField>
                </Grid>
                {local.limitByGuarantee === 1 && (
                    <>
                        {hideOverseasProfitsClaims && (
                            <Grid size={6}/>
                        )}
                        <Grid size={6}>
                            <CurrencySelect
                                variant="outlined"
                                label="Liabilities Currency (擔保貨幣)"
                                value={local.liabilitiesCurrency || 'HKD'}
                                onChange={(value) => emit({liabilitiesCurrency: value})}
                                popoverContent={liabilitiesCurrencyTip}
                            />
                        </Grid>
                        <Grid size={6}>
                            <MoneyField
                                label="Liabilities Amount (擔保总额)"
                                value={local.liabilitiesAmount}
                                onChangeNumber={(value) => emit({liabilitiesAmount: Number(value) as any})}
                                editable
                                currencySymbol={local.liabilitiesCurrency || ''}
                                showCents
                                align="center"
                                popoverContent={liabilitiesAmountTip}
                            />
                        </Grid>
                    </>
                )}
                <Grid size={12 as any}>
                    <AutocompleteField
                        fullWidth
                        align="left"
                        forcePopupIcon
                        options={OTHER_NAME_THAN_COMPANY_OPTIONS}
                        label="Other name than company (公司別稱)"
                        value={local.otherNameThanCompany || null}
                        onChange={(v) => emit({otherNameThanCompany: v || null})}
                        commitOnBlur
                        popoverContent={otherNameThanCompanyTip}
                    />
                </Grid>
                <Grid size={6 as any}>
                    <InputField select fullWidth variant="outlined" label="Section 88 (慈善機構)"
                                value={Number(local.section88) === 1 ? 1 : 0}
                                onChange={(e) => emit({section88: Number(e.target.value) === 1 ? 1 : 0})}
                                popoverContent={section88Tip}>
                        <MenuItem dense value={0}>No</MenuItem>
                        <MenuItem dense value={1}>Yes</MenuItem>
                    </InputField>
                </Grid>
                <Grid size={6 as any} id="annual-detail-field-taxFileNo">
                    <InputField
                        type="br-prefix"
                        label="Tax File No. (稅檔編號)"
                        fullWidth
                        variant="outlined"
                        error={!!taxFileNoError}
                        helperText={taxFileNoError}
                        required
                        popoverContent={
                            <Link
                                href="https://www.gov.hk/en/residents/taxes/etax/services/brn_enquiry.htm"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                GovHK: Business Registration
                            </Link>
                        }
                        value={(() => {
                            const taxFileNo = local.taxFileNo || '';
                            // 如果 taxFileNo 只是 BR 号码（不包含 /），添加 / 前缀以显示在右侧
                            // 如果已经包含 /，有前缀时右侧隐藏 -000
                            if (taxFileNo && !taxFileNo.includes('/')) {
                                return `/${taxFileNo}`;
                            }
                            if (taxFileNo.includes('/')) {
                                const [prefix] = taxFileNo.split('/', 2);
                                if ((prefix || '').trim()) {
                                    return `${prefix}/${taxFileBrDisplay}`;
                                }
                            }
                            return taxFileNo;
                        })()}
                        onChange={(e) => {
                            const value = e.target.value;
                            // If value contains /, split and check prefix
                            if (value && value.includes('/')) {
                                const parts = value.split('/', 2);
                                const prefix = parts[0] || '';
                                const brPart = parts[1] || '';
                                // If prefix is empty, save only BR part (without /)
                                const finalValue = prefix ? `${prefix}/${brPart}` : brPart;
                                emit({taxFileNo: finalValue || null});
                            } else {
                                // No separator, save as is
                                emit({taxFileNo: value || null});
                            }
                        }}
                        separator="/"
                        leftPartMaxLength={2}
                        rightPartReadonly={taxFileBrDisplay}
                    />
                </Grid>
            </Grid>
        </ExpansionPanel>
    );
}
